import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { ErrorNotice } from '../../components/ErrorNotice';
import { Icon } from '../../components/Icon';
import { RichText } from '../../components/RichText';
import { LoadingIndicator } from '../../components/Spinner';
import { usePortalArticle, usePortalKnowledge } from './queries';

/**
 * Die Wissensdatenbank im Portal -- F4-22.
 *
 * Der Kunde bekommt ausschliesslich freigegebene Artikel der Sichtbarkeit
 * `portal` oder `public` -- entschieden vom Server
 * (`knowledge/service.visible_levels`). Diese Datei ruft dieselbe Suche auf
 * wie die Agentenoberflaeche; der Unterschied liegt allein in der Rolle des
 * Aufrufers.
 *
 * **Sie sieht auch aus wie die Agentenoberflaeche.** Trefferliste links,
 * Artikel rechts, dieselben Klassen (``result``, ``prose--rich``). Vorher
 * trug jeder Treffer ``history__change`` -- einen ``display: contents``-Helfer
 * fuer Definitionslisten, der das Listenelement aufloest: Titel und Anriss
 * wurden je zu einem eigenen Streifen ueber die volle Breite. Und der Text des
 * Artikels lief durch ``.prose`` mit ``white-space: pre-wrap``, sodass jeder
 * Zeilenumbruch der HTML-Quelle zusaetzlich im Bild stand.
 */
export function PortalKnowledge() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [articleId, setArticleId] = useState<string | null>(null);

  const results = usePortalKnowledge(query);
  const article = usePortalArticle(articleId);

  return (
    <>
      <PageHeader title={t('portal.knowledge')} />

      <div className="kb-workspace">
        <div className="kb-list">
          <div className="kb-search">
            <span className="kb-search__icon">
              <Icon name="search" />
            </span>
            <input
              id="portal-kb-search"
              type="search"
              className="field__control kb-search__control"
              value={query}
              aria-label={t('app.search')}
              placeholder={t('portal.knowledgePlaceholder')}
              onChange={(event) => {
                setQuery(event.target.value);
                setArticleId(null);
              }}
            />
          </div>

          {results.isError ? <ErrorNotice error={results.error} /> : null}

          <ul className="result-list" aria-label={t('portal.knowledge')}>
            {results.isPending && query.trim().length >= 3 ? (
              <li>
                <LoadingIndicator lines={6} />
              </li>
            ) : null}

            {results.data?.length === 0 ? (
              <li>
                <EmptyState
                  icon="knowledge"
                  title={t('portal.noArticles')}
                  hint={t('portal.noArticlesHint')}
                />
              </li>
            ) : null}

            {(results.data ?? []).map((item) => (
              <li
                key={item.id}
                className={articleId === item.id ? 'result result--current' : 'result'}
              >
                <button
                  type="button"
                  className="result__title"
                  aria-current={articleId === item.id ? 'true' : undefined}
                  onClick={() => {
                    setArticleId(item.id);
                  }}
                >
                  {item.title}
                </button>
                {item.summary ? <span className="result__summary">{item.summary}</span> : null}
              </li>
            ))}
          </ul>
        </div>

        <aside className="kb-detail">
          {articleId === null ? (
            <EmptyState
              icon="knowledge"
              title={t('portal.chooseArticle')}
              hint={t('portal.chooseArticleHint')}
            />
          ) : null}

          {article.isPending && articleId !== null ? (
            <div style={{ padding: 'var(--space-6)' }}>
              <LoadingIndicator lines={8} />
            </div>
          ) : null}

          {article.data ? (
            <>
              <header className="kb-article__head">
                <h2 className="kb-article__title">{article.data.title}</h2>
              </header>
              <div style={{ padding: 'var(--space-6)' }}>
                {/* Das HTML ist beim Speichern durch `core/html.sanitise_html`
                    gelaufen. `RichText` prueft beim Anzeigen ein zweites Mal
                    und setzt nichts ein, sondern baut Elemente -- im Portal
                    wiegt das schwerer als anderswo: Hier liest jemand von
                    aussen. */}
                <RichText html={article.data.body_html} />
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </>
  );
}
